/**
 * Theme resolver - converts game theme input to derived theme
 */

import type { ThemeMode, GameThemeInput, CoreThemeTokens, DerivedTheme, PlayerColorTokens, PartialCoreThemeTokens } from '@merels/games/types/theme'
import { deriveCoreTokensFromAccent, deriveTheme } from '@merels/games/types/theme-derivation'

/**
 * Default player colors when not provided (coral theme)
 */
function getDefaultPlayerColors(mode: ThemeMode): PlayerColorTokens[] {
  if (mode === 'light') {
    return [
      { primary: '#ff6b6b', background: '#ffe0e0', foreground: '#cc5555' }, // Coral red
      { primary: '#ff8e53', background: '#ffe8d6', foreground: '#cc6f42' }, // Coral orange
      { primary: '#ff7f9f', background: '#ffe0e8', foreground: '#cc5f7f' }, // Coral pink
      { primary: '#ff9f7f', background: '#ffe8e0', foreground: '#cc7f5f' }, // Peach coral
      { primary: '#ff6b9d', background: '#ffe0ea', foreground: '#cc557d' }, // Pink coral
    ]
  } else {
    return [
      { primary: '#ff6b6b', background: '#4a1f1f', foreground: '#ffa8a8' }, // Coral red
      { primary: '#ff8e53', background: '#4a2f1f', foreground: '#ffb88a' }, // Coral orange
      { primary: '#ff7f9f', background: '#4a1f2f', foreground: '#ffa8c8' }, // Coral pink
      { primary: '#ff9f7f', background: '#4a2f1f', foreground: '#ffb89f' }, // Peach coral
      { primary: '#ff6b9d', background: '#4a1f2a', foreground: '#ffa8bd' }, // Pink coral
    ]
  }
}

/**
 * Resolve game theme input to derived theme for a specific mode
 */
export function resolveTheme(
  gameThemeInput: GameThemeInput | null,
  mode: ThemeMode
): DerivedTheme {
  const themeData = gameThemeInput?.themes?.[mode]
  
  if (!themeData) {
    // No game theme - use defaults
    return resolveThemeFromTokens(null, mode, getDefaultPlayerColors(mode), null)
  }
  
  const tokens = themeData.tokens
  const playerColors = themeData.playerColors || getDefaultPlayerColors(mode)
  const neutral = themeData.neutral ?? null
  
  return resolveThemeFromTokens(tokens, mode, playerColors, neutral)
}

/**
 * Resolve theme from core tokens (or derive from accent)
 */
function resolveThemeFromTokens(
  tokens: PartialCoreThemeTokens | null | undefined,
  mode: ThemeMode,
  playerColors: PlayerColorTokens[],
  neutral: PlayerColorTokens | null
): DerivedTheme {
  let core: CoreThemeTokens
  const providedByGame: DerivedTheme['providedByGame'] = {
    accent: false,
    appBackground: false,
    surfaceBackground: false,
    textPrimary: false,
    danger: false,
  }
  
  if (!tokens || Object.keys(tokens).length === 0 || (Object.keys(tokens).length === 1 && 'accent' in tokens && tokens.accent)) {
    // Only accent provided (or nothing) - derive everything from accent
    const accent = tokens?.accent || (mode === 'light' ? '#ff6b6b' : '#ff6b6b') // Default coral
    const derived = deriveCoreTokensFromAccent(accent, mode)
    core = { accent, ...derived }
    providedByGame.accent = !!tokens?.accent
  } else {
    // Partial or full tokens provided
    const derived = deriveCoreTokensFromAccent(tokens.accent, mode)
    core = {
      accent: tokens.accent,
      appBackground: ('appBackground' in tokens && tokens.appBackground) ? tokens.appBackground : derived.appBackground,
      surfaceBackground: ('surfaceBackground' in tokens && tokens.surfaceBackground) ? tokens.surfaceBackground : derived.surfaceBackground,
      textPrimary: ('textPrimary' in tokens && tokens.textPrimary) ? tokens.textPrimary : derived.textPrimary,
      danger: ('danger' in tokens && tokens.danger) ? tokens.danger : derived.danger,
    }
    providedByGame.accent = true
    providedByGame.appBackground = 'appBackground' in tokens && !!tokens.appBackground
    providedByGame.surfaceBackground = 'surfaceBackground' in tokens && !!tokens.surfaceBackground
    providedByGame.textPrimary = 'textPrimary' in tokens && !!tokens.textPrimary
    providedByGame.danger = 'danger' in tokens && !!tokens.danger
  }
  
  return deriveTheme(core, playerColors, mode, providedByGame, neutral)
}

